import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Table } from 'reactstrap';
import DesignMixPickerModal from './DesignMixPickerModal';
import { getDesignMixOptions, getFinishedGoodsGrades } from '../services/ConcreteEstimatorServices';
import { getAllDesignMix } from '../services/DesignMixServices';
import { getAllProducts } from '../services/ProductServices';
import {
    filterDesignMixOptions,
    mapDesignMixListToOptions,
    mixRecipeSnapshot,
    recipeDisplayRows,
} from '../utils/concreteEstimatorDesignMix';

function RecipeBreakdownTable({ recipe }) {
    const rows = useMemo(() => recipeDisplayRows(recipe), [recipe]);
    if (!recipe) return null;

    return (
        <div className="estimator-recipe-breakdown mt-2">
            <div className="small mb-2">
                <strong>Selected mix:</strong> {recipe.label || '—'}
                {recipe.work_schedule_no && (
                    <span className="text-muted"> · Schedule {recipe.work_schedule_no} {recipe.work_schedule_date || ''}</span>
                )}
            </div>
            {recipe.mix_summary && (
                <p className="text-muted small mb-2">{recipe.mix_summary}</p>
            )}
            <Table bordered size="sm" className="estimator-design-mix-table mb-0">
                <thead>
                    <tr>
                        <th>Component</th>
                        <th className="text-right">kg/m³</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.label}>
                            <td>{row.label}</td>
                            <td className="text-right">{Number(row.value || 0).toFixed(3)}</td>
                        </tr>
                    ))}
                    <tr className="estimator-rate-group-header">
                        <td>Total Cement</td>
                        <td className="text-right">{Number(recipe.total_cem || 0).toFixed(0)}</td>
                    </tr>
                    <tr className="estimator-rate-group-header">
                        <td>Density</td>
                        <td className="text-right">{Number(recipe.density || 0).toFixed(0)}</td>
                    </tr>
                </tbody>
            </Table>
        </div>
    );
}

async function loadFinishedGoodsGrades(cookies) {
    try {
        const res = await getFinishedGoodsGrades(cookies);
        const grades = (res.grades || []).filter(Boolean);
        if (grades.length) return grades;
    } catch (err) {
        // fallback below
    }
    try {
        const res = await getAllProducts(cookies);
        return (res.product_list || [])
            .filter((product) => product.status && product.category?.name === 'FINISHED GOODS')
            .map((product) => product.name)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
    } catch (err) {
        return [];
    }
}

async function fetchDesignMixOptions(cookies, plantId, gradeFilter, searchText) {
    if (!plantId || !gradeFilter) {
        return { options: [], error: 'Select a grade first.', showingAllPlantMixes: false };
    }

    const params = {
        plant_id: plantId,
        grade: gradeFilter,
        search: searchText || undefined,
    };

    const sources = [
        async () => {
            const res = await getDesignMixOptions(cookies, params);
            return filterDesignMixOptions(res.design_mix_options || [], gradeFilter, searchText);
        },
        async () => {
            const res = await getDesignMixOptions(cookies, { plant_id: plantId, search: searchText || undefined });
            return filterDesignMixOptions(res.design_mix_options || [], gradeFilter, searchText);
        },
        async () => {
            const fallback = await getAllDesignMix(cookies, { plant_id: plantId });
            return filterDesignMixOptions(
                mapDesignMixListToOptions(fallback.design_mix_list || []),
                gradeFilter,
                searchText,
            );
        },
        async () => {
            const res = await getDesignMixOptions(cookies, { plant_id: plantId, search: searchText || undefined });
            return res.design_mix_options || [];
        },
        async () => {
            const fallback = await getAllDesignMix(cookies, { plant_id: plantId });
            return mapDesignMixListToOptions(fallback.design_mix_list || []);
        },
    ];

    let lastError = '';
    for (let index = 0; index < sources.length; index += 1) {
        try {
            const options = await sources[index]();
            if (options.length) {
                return {
                    options,
                    error: '',
                    showingAllPlantMixes: index >= 3,
                };
            }
        } catch (err) {
            lastError = err?.response?.data?.message || err?.message || lastError;
        }
    }

    return {
        options: [],
        error: lastError || `No design mix found for grade "${gradeFilter}" at this plant.`,
        showingAllPlantMixes: false,
    };
}

function DesignMixSelector({
    cookies,
    plantId,
    searchText,
    onSearchChange,
    gradeFilter,
    onGradeFilterChange,
    selectedMixId,
    selectedMixLabel,
    selectedMixRecipe,
    onSelectMix,
}) {
    const [options, setOptions] = useState([]);
    const [gradeOptions, setGradeOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [showingAllPlantMixes, setShowingAllPlantMixes] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [pickedRow, setPickedRow] = useState(null);

    const canSelectRecipe = Boolean(gradeFilter);

    useEffect(() => {
        if (!plantId) {
            setGradeOptions([]);
            return undefined;
        }
        let active = true;
        loadFinishedGoodsGrades(cookies).then((grades) => {
            if (active) setGradeOptions(grades);
        });
        return () => { active = false; };
    }, [cookies, plantId]);

    const loadOptions = useCallback(() => {
        if (!plantId || !gradeFilter) {
            setOptions([]);
            setLoadError(gradeFilter ? '' : 'Select a grade first.');
            return undefined;
        }
        setLoading(true);
        setLoadError('');
        return fetchDesignMixOptions(cookies, plantId, gradeFilter, searchText)
            .then(({ options: rows, error, showingAllPlantMixes: allPlant }) => {
                setOptions(rows);
                setLoadError(error || '');
                setShowingAllPlantMixes(Boolean(allPlant));
            })
            .finally(() => setLoading(false));
    }, [cookies, plantId, gradeFilter, searchText]);

    useEffect(() => {
        if (!showModal) return undefined;
        const timer = setTimeout(loadOptions, 200);
        return () => clearTimeout(timer);
    }, [loadOptions, showModal]);

    useEffect(() => {
        setPickedRow(null);
        setShowModal(false);
    }, [gradeFilter]);

    const selectedRow = useMemo(
        () => options.find((row) => String(row.id) === String(selectedMixId))
            || (pickedRow?.id && String(pickedRow.id) === String(selectedMixId) ? pickedRow : null),
        [options, selectedMixId, pickedRow],
    );

    const activeRecipe = useMemo(() => {
        if (pickedRow?.id && (!selectedMixId || String(pickedRow.id) === String(selectedMixId))) {
            return mixRecipeSnapshot({
                ...pickedRow,
                label: selectedMixLabel || `${pickedRow.grade || ''} · ${pickedRow.site || pickedRow.customer || ''}`.trim(),
            });
        }
        if (selectedMixRecipe?.id) {
            return {
                ...selectedMixRecipe,
                label: selectedMixRecipe.label || selectedMixLabel,
            };
        }
        if (selectedRow) {
            return mixRecipeSnapshot({
                ...selectedRow,
                label: selectedMixLabel || `${selectedRow.grade || ''} · ${selectedRow.site || selectedRow.customer || ''}`.trim(),
            });
        }
        if (selectedMixId && selectedMixLabel) {
            return { id: selectedMixId, label: selectedMixLabel };
        }
        return null;
    }, [pickedRow, selectedMixRecipe, selectedMixLabel, selectedMixId, selectedRow]);

    const openPopup = (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (!canSelectRecipe) return;
        setShowModal(true);
        loadOptions();
    };

    const closePopup = () => setShowModal(false);

    const onSubmitMix = useCallback((row) => {
        if (!row?.id) return;
        setPickedRow(row);
        onSelectMix?.(row);
        setShowModal(false);
    }, [onSelectMix]);

    const modal = (
        <DesignMixPickerModal
            show={showModal}
            loading={loading}
            options={options}
            loadError={loadError}
            gradeFilter={gradeFilter}
            searchText={searchText}
            onSearchChange={onSearchChange}
            onRefresh={loadOptions}
            onSelect={onSubmitMix}
            onClose={closePopup}
            showingAllPlantMixes={showingAllPlantMixes}
        />
    );

    if (!plantId) {
        return (
            <p className="text-muted small mb-2">Select plant to load design mix options.</p>
        );
    }

    return (
        <div className="estimator-design-mix-panel mb-3">
            <div className="estimator-recipe-details-header form-control-panel mb-2">
                <h5 className="mb-2 estimator-recipe-details-title">
                    <b>Recipe Details</b>
                </h5>
                <div className="estimator-recipe-details-toolbar">
                    <div className="estimator-recipe-grade-group">
                        <label className="small font-weight-bold mb-0" htmlFor="estimator-recipe-grade">
                            Grade
                        </label>
                        <select
                            id="estimator-recipe-grade"
                            className="form-control form-control-sm estimator-recipe-grade-select"
                            value={gradeFilter || ''}
                            onChange={(e) => onGradeFilterChange?.(e.target.value)}
                        >
                            <option value="">Select Grade</option>
                            {gradeOptions.map((grade) => (
                                <option key={grade} value={grade}>{grade}</option>
                            ))}
                        </select>
                    </div>
                    <Button
                        type="button"
                        color="primary"
                        size="sm"
                        className="estimator-recipe-select-btn"
                        disabled={!canSelectRecipe}
                        onClick={openPopup}
                    >
                        {selectedMixId ? 'Change Recipe' : 'Select Recipe'}
                    </Button>
                </div>
            </div>

            {activeRecipe?.id ? (
                <RecipeBreakdownTable recipe={activeRecipe} />
            ) : (
                <p className="text-muted small mb-0">
                    {canSelectRecipe
                        ? <>Click <strong>Select Recipe</strong> to choose a design mix for <strong>{gradeFilter}</strong>.</>
                        : <>Select <strong>Grade</strong> first, then click <strong>Select Recipe</strong> to pick the mix.</>}
                </p>
            )}

            {modal}
        </div>
    );
}

export default DesignMixSelector;
